import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogOut, Menu, X } from 'lucide-react';

import ColorModeIconDropdown from '@/theme/ColorModeIconDropdown';
import LanguageSelectorDropdown from '@/theme/LanguageSelectorDropdown';
import { GLASS_ICON_BUTTON } from '@/components/shared/glass';
import { cn } from '@/lib/utils';
import BrandMark from './BrandMark';
import MenuContent from './MenuContent';
import SearchTrigger from './SearchTrigger';
import UserMenu from './UserMenu';
import { NavModel } from './nav-model';

const rowClass =
    'flex h-9 items-center gap-2.5 rounded-md px-2.5 text-[13px] font-medium text-muted-foreground outline-none transition-colors hover:bg-surface-hover hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring';

/**
 * Mobile (< md): a compact glass bar — menu at the start, brand mark,
 * search and the user chip at the end — and an off-canvas drawer that
 * opens from the start edge (right in RTL) with the inline menu and
 * language, theme and log out in its footer. Built on Radix Dialog, so
 * focus trapping, Escape and outside-click come for free.
 */
export default function MobileNav({
    model,
    canSearch,
    logoUrl,
    systemName,
    userName,
    userEmail,
    onLogout,
}: {
    model: NavModel;
    canSearch: boolean;
    logoUrl?: string | null;
    systemName?: string | null;
    userName?: string | null;
    userEmail?: string | null;
    onLogout: () => void;
}) {
    const { t } = useTranslation('crm');
    const [open, setOpen] = React.useState(false);
    const close = () => setOpen(false);

    return (
        <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
            <div className="flex h-14 shrink-0 items-center gap-2 border-b border-glass-nav-border bg-glass-nav px-3 backdrop-blur-xl backdrop-saturate-150 md:hidden">
                <DialogPrimitive.Trigger asChild>
                    <button
                        type="button"
                        aria-label={t('chrome.openMenu')}
                        className={cn(GLASS_ICON_BUTTON, 'flex items-center justify-center')}
                    >
                        <Menu />
                    </button>
                </DialogPrimitive.Trigger>
                <Link to="/" aria-label={t('chrome.goToDashboard')} className="flex items-center">
                    <BrandMark logoUrl={logoUrl} name={systemName} compact />
                </Link>
                <div className="flex-1" />
                {canSearch && <SearchTrigger iconOnly />}
                <UserMenu name={userName} email={userEmail} onLogout={onLogout} showName={false} />
            </div>

            <DialogPrimitive.Portal>
                <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0 md:hidden" />
                <DialogPrimitive.Content
                    aria-describedby={undefined}
                    className="fixed inset-y-0 start-0 z-50 flex w-80 max-w-[85vw] flex-col border-e border-border bg-background shadow-lg outline-none data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0 md:hidden"
                >
                    <DialogPrimitive.Title className="sr-only">
                        {t('chrome.openMenu')}
                    </DialogPrimitive.Title>
                    <div className="flex h-14 shrink-0 items-center justify-between border-b border-border-subtle px-4">
                        <BrandMark logoUrl={logoUrl} name={systemName} />
                        <DialogPrimitive.Close asChild>
                            <button
                                type="button"
                                aria-label={t('chrome.closeMenu')}
                                className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-surface-hover hover:text-foreground"
                            >
                                <X className="size-4" />
                            </button>
                        </DialogPrimitive.Close>
                    </div>
                    <div className="min-h-0 flex-1">
                        <MenuContent model={model} variant="drawer" onNavigate={close} />
                    </div>
                    <div className="flex shrink-0 items-center gap-1 border-t border-border-subtle p-2">
                        <button
                            type="button"
                            onClick={() => {
                                close();
                                onLogout();
                            }}
                            className={cn(rowClass, 'me-auto flex-1')}
                        >
                            <LogOut aria-hidden="true" className="size-4" />
                            {t('chrome.logOut')}
                        </button>
                        <LanguageSelectorDropdown compact className="h-8 w-8 [&_svg]:size-4" />
                        <ColorModeIconDropdown />
                    </div>
                </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
    );
}
